import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Loader2, Maximize2 } from 'lucide-react';
import {
  lightMode, mountMesh, rememberLight, type LightMode, type LoadedMesh, type Shot,
} from './meshScene';
import { SegmentedToggle } from './SegmentedToggle';
import { attachDrawModes, type DrawMode } from './meshDrawModes';
import { ViewTurnCorner } from './ViewTurnCorner';
import type { Dims, Extent } from './meshAnnotations';
import { useLanguage } from '../../contexts/LanguageContext';

// How the mesh is drawn — the two modes and what each is for are argued in meshDrawModes,
// which builds them. This is the row of buttons over them.
type Mode = DrawMode;

export const MODES: { value: Mode; label: string }[] = [
  { value: 'textured', label: 'Textured' },
  { value: 'wireframe', label: 'Wireframe' },
  { value: 'xray', label: 'See-through' },
];

// WHAT MARKS A VIEWPORT IN THE DOM, and how to find one from a click. Spread onto the panel
// a mesh is drawn in — by this viewer and by PartViewer, which is a second viewer for the same
// job (see the note at the top of it).
//
// It exists for the dialogs that open OVER a mesh and want to leave it usable: the turn dialog
// dismisses itself on a click outside, and "outside" has to mean outside the mesh as well as
// outside itself, or naming the faces of a part while turning it round would shut the boxes at
// the first drag. The attribute and the selector together, because they are one fact and the
// way they fail apart is a dialog that never closes.
export const VIEWPORT_MARK = { 'data-mesh-viewport': true } as const;
export const VIEWPORT_SELECTOR = '[data-mesh-viewport]';

// HOW IT IS LIT, on the same row as how it is drawn — see LightMode, which argues the two
// answers. Both controls are about the VIEW rather than about the mesh, which is why they
// sit together and why neither of them changes a byte of the file.
const LIGHTS: { value: LightMode; label: string }[] = [
  { value: 'even', label: 'Even' },
  { value: 'studio', label: 'Studio' },
];

// What the mesh actually is, once loaded. Shown next to the viewer because it is the
// number the decimation target is aiming at — the setting says what was asked for, this
// says what came back, and they are not always the same.
type MeshStats = { vertices: number; triangles: number };

// HOW BIG THE MESH IS, to the nearest thousand. "39,963 vertices · 38,988 triangles" is six
// digits of precision on a number nobody acts on to the unit: what it answers is "is this a
// reconstruction or a handful of boxes", and the last three digits are noise dressed as
// accuracy. Under a thousand it is printed whole, where the exact figure is short enough to
// read and the rounding would say 0K.
export const meshCount = (n: number): string =>
  (n < 1000 ? String(n) : `${Math.round(n / 1000)}K`);

// A generated mesh, turned and zoomed with the mouse, drawn textured or as bare
// topology.
//
// `src` is our own /api/notice/3d/model/<id> and not fal's URL: fal's expires, and a
// bench whose old runs stop opening is a bench that cannot compare anything.
export function MeshViewer({
  src, dims, trueScale, topology = true, lights = true, measurements = true, shared = true,
  onMeasure, onHoverUv, onMesh, onExpand, corner, keepView, className = '',
}: {
  src: string;
  // The part's real size, drawn as measurement lines around the object. Every axis it
  // does not know is still drawn, labelled unknown — the point of the lines is telling
  // which of the three you have.
  dims?: Dims;
  // The mesh is already in real units (a CAD conversion), so it needs no anchor.
  trueScale?: boolean;
  // Whether HOW THE MESH IS BUILT is on offer: the draw-mode toggle and the counts.
  //
  // On by default, because on the bench pages that IS the question — a reconstruction is
  // judged on where it spent its triangles, and the texture is very good at hiding that.
  // Off where the question is a different one. In a notice's own 3D panel you are asking
  // "is this the right part, and is it the right size": there the toggle is two modes
  // nobody picks sitting above the object, and the vertex count is a number with nothing
  // to compare it against. Both are still one click away — the expanded viewer keeps them,
  // which is where a mesh gets looked at rather than glanced at.
  topology?: boolean;
  // WHETHER THE LIGHTING IS ON OFFER: even against studio, see LightMode.
  //
  // On by default, because the choice matters most where a mesh is being judged and that is
  // most of these. Off for a viewport with a job of its own to get on with — the retouch
  // panel's model is there to be pointed at, its atlas is the thing being read, and a second
  // control above it costs height in a panel already short of it. Off does not mean unlit:
  // it means lit however the last viewer was left, which is the point of remembering.
  lights?: boolean;
  // The mesh's extents in the file's own units. Handed up so the page can offer to
  // rescale it: the numbers on screen are this times the millimetre scale.
  // THE MEASUREMENT LINES AROUND THE OBJECT. On by default, because judging whether a mesh
  // is the right SIZE is most of what a viewer here is for. Off for a view that exists only
  // to be pointed at: with nothing measured they draw six labels reading "?" across the
  // frame, which is clutter over the thing being looked at and work on every frame.
  measurements?: boolean;
  // WHETHER THIS VIEWER JOINS THE ANGLE EVERY MESH IN THE APP IS TURNED TO. On by default:
  // the panel and the modal are one object seen twice and should agree. Off for a small
  // aside, which otherwise DRIVES that angle — so turning it to find a face would swing
  // every thumbnail on the page, and each settle writes to storage and wakes them all.
  shared?: boolean;
  // THE LOADED OBJECT, for a caller that has its own work to do on it. Forwarded rather
  // than reached for: this file already builds wireframes off the same callback, and a
  // second consumer of it should not have to mount its own viewer to get one.
  onMesh?: (mesh: LoadedMesh) => void;
  onMeasure?: (size: Extent) => void;
  // Where the pointer is on this mesh's texture, for a view showing that texture beside it.
  // See MountOpts.onHoverUv.
  onHoverUv?: (uv: [number, number] | null) => void;
  // Open this mesh bigger. Given, a control appears in the viewport's corner; withheld,
  // there is none — which is what the modal's own viewer wants, being already as big as
  // it goes.
  onExpand?: () => void;
  // A control in the top-left of the VIEWPORT — over the white, beside the object rather
  // than in the page around it. For the one switch that is about this mesh as an asset
  // rather than about its geometry: whether it is the part's model in the Parts DB.
  corner?: ReactNode;
  // WHAT MAKES TWO MESHES THE SAME THING TO LOOK AT. While this is unchanged, a new `src`
  // keeps the camera where it was standing rather than framing itself afresh — which is
  // what comparing a written run's two passes needs, and what stepping to a different run
  // must not do. Absent, every mount frames anew, which is what it always did.
  keepView?: string;
  className?: string;
}) {
  const { t } = useLanguage();
  const host = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<Mode>('textured');
  // Opened at whatever the last viewer was left on, and not reset per mesh — see lightMode.
  const [lit, setLit] = useState<LightMode>(lightMode);
  const [stats, setStats] = useState<MeshStats | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  // Set by the scene once it exists, so the mode buttons drive a scene that is already
  // on screen instead of rebuilding it — swapping materials must not reset the camera,
  // or comparing textured against wireframe means re-aiming at the object every click.
  const applyMode = useRef<((m: Mode) => void) | null>(null);
  // The loaded scene's camera controls, for the buttons in the corner. Nulled on unmount
  // with applyMode, so a click landing after a mesh has been swapped out drives nothing.
  const aim = useRef<LoadedMesh | null>(null);
  // The shot the last mesh was left at, and which view it belonged to. Held across the
  // unmount that a new `src` causes, and spent on the next scene only if it is the same
  // view — see keepView.
  const held = useRef<{ key: string; shot: Shot } | null>(null);
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    // Back to loading for every new mesh. mountMesh reports 'ready' or 'failed' when it
    // knows, and until then the state belonged to the mesh before this one.
    setState('loading');
    const unmount = mountMesh(el, src, {
      interactive: true,
      axes: true,
      // WHICH ARROW IS WHICH MEASUREMENT, written on the arrows. The gizmo says which way
      // the object is turned and, with three coloured letters alone, nothing about which of
      // its numbers is which — "is the green one the height" is a question asked on every
      // mesh, and the answer was only findable in the dimension labels hanging off the
      // object, which half the viewers here do not draw.
      axisWords: {
        x: t('notice.3d.width'),
        y: t('notice.3d.height'),
        z: t('notice.3d.depth'),
      },
      dims,
      trueScale,
      // This is the view that DRIVES the shared angle: it is the one with a mouse on it.
      // Which means expanding a mesh into the modal opens on the angle the panel was left
      // at, and the thumbnails underneath follow both.
      sharedView: shared,
      dimLabels: measurements ? {
        wanted: {
          width: t('notice.3d.wanted'),
          depth: t('notice.3d.wanted'),
          height: t('notice.3d.wanted'),
        },
        actual: {
          width: t('notice.3d.actualWidth'),
          depth: t('notice.3d.actualDepth'),
          height: t('notice.3d.actualHeight'),
        },
        unknown: t('notice.3d.unknown'),
      } : undefined,
      onMeasure,
      onHoverUv,
      onState: setState,
      onLoaded: (mesh) => {
        const { vertices, triangles, apply } = attachDrawModes(mesh.THREE, mesh.root);
        setStats({ vertices, triangles });
        applyMode.current = apply;
        apply(mode);
        aim.current = mesh;
        // BACK TO WHERE THE LAST ONE WAS STANDING, when the two are the same thing to look
        // at. Applied here rather than before the load because there is no camera to move
        // until the scene exists, and applied only for a matching key so stepping to
        // another run still opens framed.
        const kept = held.current;
        if (keepView && kept?.key === keepView) mesh.aimAt(kept.shot);
        onMesh?.(mesh);
      },
    });

    return () => {
      // Taken before the scene goes, because after it there is nothing to ask. Kept under
      // the key it was taken at, so it is only ever spent on a mesh it belongs with.
      if (keepView && aim.current) {
        held.current = { key: keepView, shot: aim.current.viewpoint() };
      }
      applyMode.current = null;
      aim.current = null;
      unmount();
    };
    // Rebuilt per mesh only. `mode` is read once for the initial material state and then
    // driven through applyMode — listing it here would tear the scene down on every
    // click of the toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, trueScale, measurements, shared, dims?.x, dims?.y, dims?.z]);

  useEffect(() => {
    applyMode.current?.(mode);
  }, [mode]);

  // Applied to the scene that is already up rather than by rebuilding it, so the camera does
  // not move: the two lightings are worth comparing, and comparing them means the same view.
  // Remembered module-side so the next mesh opened is lit the way this one was left.
  useEffect(() => {
    rememberLight(lit);
    aim.current?.light(lit);
  }, [lit]);

  return (
    // A COLUMN, so the viewport underneath can be told to take what is left rather than a
    // fixed height. Nothing changes where the caller sizes it outright (a panel, a
    // thumbnail); it is what lets the modal hand it the room its controls did not use, and
    // take that room back on a short screen instead of running off the bottom of it.
    <div className="flex min-h-0 flex-col">
      {/* The counts sit on their own line UNDER the toggle, not beside it: they are a
          fact about the mesh, not a fourth thing to click, and inline they read as part
          of the control. Both go together when `topology` is off — they are one answer,
          to a question that panel is not asking. */}
      {(topology || lights) && (
        <div className="mb-2 flex shrink-0 flex-col items-center gap-1.5">
          {/* HOW IT IS DRAWN, AND HOW IT IS LIT — a row each, stacked. Two questions about
              the view and none about the mesh, and they are the two things you change while
              looking at the same part rather than while choosing a different one.
              THEY SHARED A ROW, and side by side the four buttons read as one control with
              four settings: two joined groups, a gap between them no wider than the gaps
              inside them, and nothing to say that Wireframe and Even answer different
              questions. One question per row says it without a label.
              Either can be switched off by its caller; a panel that has neither draws
              nothing at all. */}
          <div className="flex flex-col items-center gap-1.5">
            {topology && <SegmentedToggle value={mode} options={MODES} onChange={setMode} />}
            {lights && <SegmentedToggle value={lit} options={LIGHTS} onChange={setLit} />}
          </div>
          {topology && stats && (
            <span className="text-xs text-slate-500">
              {meshCount(stats.vertices)} vertices · {meshCount(stats.triangles)} triangles
            </span>
          )}
        </div>
      )}

      {/* White in both themes, deliberately. This panel is not page furniture — it is
          where a mesh is judged, and the meshes are dark wood: on the dark theme's navy
          they were a silhouette, which is the one thing that must not be hidden here.
          The wireframe colours below are chosen against white for the same reason. */}
      {/* MARKED (see VIEWPORT_MARK) so a dialog opened over the mesh can tell a click on the
          part from a click anywhere else. The panel and not the canvas inside it: the gizmo,
          the view buttons and the expand corner sit on top and are as much the viewer as the
          mesh is. */}
      <div
        {...VIEWPORT_MARK}
        className={`relative overflow-hidden rounded-xl border border-slate-300 bg-white ${className}`}
      >
        <div ref={host} className="h-full w-full" />
        {/* Bottom right, like the part viewer's — see PartViewer, where the reason is
            written. The two are one control on two pages and should not be hunted for. */}
        {corner && <div className="absolute bottom-2 right-2">{corner}</div>}

        {/* AIMING THE VIEW WITHOUT DRAGGING IT. A drag is how you look for something and a
            poor way to arrive somewhere known — "the other side" and "back to how it opened"
            are two places you go over and over here, and by hand each is a wrist's worth of
            orbiting that lands somewhere NEAR. Which for the face selection below matters:
            the mask is taken from wherever the camera happens to be.

            TOP RIGHT, the last corner still free. The gizmo has the bottom left, expanding
            has the bottom right, and `corner` has the top left. Only once the mesh is up:
            these turn a camera, and there is no camera until then. */}
        {state === 'ready' && (
          <ViewTurnCorner
            className="absolute right-2 top-2"
            onHome={() => aim.current?.home()}
            onTurn={(axis, degrees) => aim.current?.turn(axis, degrees)}
          />
        )}
        {state === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        )}
        {/* A FAILURE IS DRAWN OVER THE VIEWPORT, not instead of it, and that is the whole
            fix for a bug that looked like three broken meshes. Returning this markup early
            unmounted the div the scene mounts INTO — so the next mesh found `host.current`
            null, mounted nothing, and left the failure on screen for ever. Deleting one run
            was enough to make every other run in the strip unopenable until a reload. */}
        {state === 'failed' && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-50 p-6 text-center text-sm text-slate-500">
            The 3D viewer could not load this mesh. The file itself may still be fine —
            download it and open it in your own viewer.
          </div>
        )}
        {/* Open it bigger. Bottom RIGHT, the one corner the scene keeps clear: the axis
            gizmo sits bottom left and the measurement labels hang off the top and the
            sides, and a control over either would be a control you cannot read.

            Inside the viewport rather than under it, because it is about this object and
            not about the page — and small, since the panel is where a mesh is judged and
            the button is only for when panel-sized is not enough. */}
        {/* A mesh that failed to load never reaches here — that is its own message, above. */}
        {onExpand && (
          <button
            type="button"
            onClick={onExpand}
            title={t('notice.3d.expand')}
            aria-label={t('notice.3d.expand')}
            className="absolute bottom-2 right-2 rounded-lg border border-slate-300 bg-white/90 p-1.5 text-slate-500 shadow-sm backdrop-blur transition-colors hover:border-blue-500 hover:text-blue-600"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
